export const CANOPY_MAX_NEIGHBORS = 10;

export type CanopyNodeRole = 'query' | 'neighbor' | 'ancestor';

export type CanopyNode = {
  id: string;
  label: string;
  role: CanopyNodeRole;
  score: number | null;
  inDisgenet?: boolean;
};

export type CanopyEdge = {
  child: string;
  parent: string;
};

export type CanopyResponse = {
  query: string;
  nodes: CanopyNode[];
  edges: CanopyEdge[];
};

export type CanopyLinkKind = 'parent' | 'child' | 'via-neighbor';

export type CanopyTreeDatum = {
  id: string;
  label: string;
  role: CanopyNodeRole;
  score: number | null;
  inDisgenet?: boolean;
  linkKind?: CanopyLinkKind;
  viaId?: string;
  children?: CanopyTreeDatum[];
};

export type CanopyTreeBuildResult = {
  tree: CanopyTreeDatum;
  neighborCount: number;
  edgeCount: number;
  edges: CanopyEdge[];
};

export const MOCK_CANOPY_RESPONSE: CanopyResponse = {
  query: 'C0002395',
  nodes: [
    { id: 'C0002395', label: "Alzheimer's disease", role: 'query', score: null, inDisgenet: true },
    { id: 'C0011268', label: 'Senile dementia', role: 'neighbor', score: 0.979377, inDisgenet: true },
    { id: 'C0011265', label: 'Presenile dementia', role: 'neighbor', score: 0.975159, inDisgenet: true },
    {
      id: 'C0338632',
      label: 'Dementia associated with another disease',
      role: 'neighbor',
      score: 0.949411,
      inDisgenet: false,
    },
    {
      id: 'C0154669',
      label: 'Senile degeneration of brain',
      role: 'neighbor',
      score: 0.933812,
      inDisgenet: true,
    },
    {
      id: 'C0241816',
      label: 'Global brain atrophy',
      role: 'neighbor',
      score: 0.921173,
      inDisgenet: true,
    },
    { id: 'C0497327', label: 'Dementia', role: 'neighbor', score: 0.911654, inDisgenet: true },
    {
      id: 'C0154668',
      label: 'Other cerebral degenerations',
      role: 'neighbor',
      score: 0.906522,
      inDisgenet: true,
    },
    {
      id: 'C0011269',
      label: 'Dementia, Vascular',
      role: 'neighbor',
      score: 0.89194,
      inDisgenet: true,
    },
    {
      id: 'C5779573',
      label: 'Primary degenerative dementia of the Alzheimer type',
      role: 'neighbor',
      score: 0.889091,
      inDisgenet: true,
    },
    {
      id: 'C0494463',
      label: 'Primary progressive dementia of the Alzheimer type',
      role: 'neighbor',
      score: 0.887394,
      inDisgenet: true,
    },
  ],
  edges: [
    { child: 'C0002395', parent: 'C0011268' },
    { child: 'C0002395', parent: 'C0154668' },
    { child: 'C0002395', parent: 'C0154669' },
    { child: 'C0002395', parent: 'C0241816' },
    { child: 'C0002395', parent: 'C0497327' },
    { child: 'C0011265', parent: 'C0002395' },
    { child: 'C0011269', parent: 'C0011265' },
    { child: 'C0338632', parent: 'C0011265' },
    { child: 'C0494463', parent: 'C0002395' },
    { child: 'C5779573', parent: 'C0002395' },
  ],
};

function scoreValue(score: number | null | undefined): number {
  if (typeof score !== 'number' || !Number.isFinite(score)) return Number.NEGATIVE_INFINITY;
  return score;
}

function normalizeRole(role: unknown, id: string, queryId: string): CanopyNodeRole {
  if (role === 'query' || id === queryId) return 'query';
  if (role === 'ancestor') return 'ancestor';
  return 'neighbor';
}

function toTreeNode(node: CanopyNode): CanopyTreeDatum {
  return {
    id: node.id,
    label: node.label || node.id,
    role: node.role,
    score: node.score,
    inDisgenet: node.inDisgenet,
  };
}

function expandKeptIdsWithBridgeNodes(
  keptIds: Set<string>,
  edges: CanopyEdge[] | undefined,
  nodesById: Map<string, CanopyNode>,
): void {
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const edge of edges ?? []) {
      const parent = String(edge?.parent ?? '');
      const child = String(edge?.child ?? '');
      if (!parent || !child || parent === child) continue;
      if (!nodesById.has(parent) || !nodesById.has(child)) continue;

      if (keptIds.has(parent) && !keptIds.has(child)) {
        const missing = nodesById.get(child);
        if (missing?.role === 'ancestor') {
          keptIds.add(child);
          expanded = true;
        }
      } else if (keptIds.has(child) && !keptIds.has(parent)) {
        const missing = nodesById.get(parent);
        if (missing?.role === 'ancestor') {
          keptIds.add(parent);
          expanded = true;
        }
      }
    }
  }
}

export function buildCanopyTreeData(
  response: CanopyResponse | null | undefined,
  maxNeighbors: number = CANOPY_MAX_NEIGHBORS,
): CanopyTreeBuildResult | null {
  if (!response?.query || !Array.isArray(response.nodes) || response.nodes.length === 0) {
    return null;
  }

  const queryId = String(response.query);
  const nodesById = new Map<string, CanopyNode>();
  for (const node of response.nodes) {
    if (!node?.id) continue;
    const id = String(node.id);
    nodesById.set(id, {
      id,
      label: String(node.label ?? id),
      role: normalizeRole(node.role, id, queryId),
      score: typeof node.score === 'number' && Number.isFinite(node.score) ? node.score : null,
      inDisgenet: node.inDisgenet === true,
    });
  }

  const queryNode = nodesById.get(queryId) ?? {
    id: queryId,
    label: queryId,
    role: 'query' as const,
    score: null,
  };

  const rankedNeighbors = [...nodesById.values()]
    .filter(node => node.id !== queryId && node.role !== 'ancestor')
    .sort((a, b) => scoreValue(b.score) - scoreValue(a.score))
    .slice(0, Math.max(0, maxNeighbors));

  const keptIds = new Set<string>([queryId, ...rankedNeighbors.map(node => node.id)]);
  expandKeptIdsWithBridgeNodes(keptIds, response.edges, nodesById);

  const keptEdges: CanopyEdge[] = [];
  for (const edge of response.edges ?? []) {
    const parent = String(edge?.parent ?? '');
    const child = String(edge?.child ?? '');
    if (!parent || !child || parent === child) continue;
    if (!keptIds.has(parent) || !keptIds.has(child)) continue;
    keptEdges.push({ parent, child });
  }

  const linkKindById = new Map<string, CanopyLinkKind>();
  const viaIdById = new Map<string, string>();

  for (const edge of keptEdges) {
    if (edge.parent === queryId) {
      linkKindById.set(edge.child, 'child');
    } else if (edge.child === queryId) {
      linkKindById.set(edge.parent, 'parent');
    } else if (!linkKindById.has(edge.child)) {
      linkKindById.set(edge.child, 'via-neighbor');
      viaIdById.set(edge.child, edge.parent);
    }
  }

  const displayNodes = [...keptIds]
    .filter(id => id !== queryId)
    .map(id => nodesById.get(id)!)
    .sort((a, b) => {
      if (a.role === 'ancestor' && b.role !== 'ancestor') return 1;
      if (b.role === 'ancestor' && a.role !== 'ancestor') return -1;
      return scoreValue(b.score) - scoreValue(a.score);
    });

  const displayById = new Map(displayNodes.map(node => [node.id, node]));
  const nestedChildrenByParent = new Map<string, CanopyNode[]>();
  for (const edge of keptEdges) {
    if (edge.parent === queryId || edge.child === queryId) continue;
    const child = displayById.get(edge.child);
    if (!child) continue;
    const children = nestedChildrenByParent.get(edge.parent) ?? [];
    children.push(child);
    nestedChildrenByParent.set(edge.parent, children);
  }

  const orderedNodes: CanopyNode[] = [];
  const addedIds = new Set<string>();
  for (const node of displayNodes) {
    if (addedIds.has(node.id)) continue;
    orderedNodes.push(node);
    addedIds.add(node.id);
    for (const child of nestedChildrenByParent.get(node.id) ?? []) {
      if (addedIds.has(child.id)) continue;
      orderedNodes.push(child);
      addedIds.add(child.id);
    }
  }

  const tree: CanopyTreeDatum = {
    ...toTreeNode(queryNode),
    children: orderedNodes.map(node => {
      const datum = toTreeNode(node);
      datum.linkKind = linkKindById.get(node.id) ?? (node.role === 'ancestor' ? 'via-neighbor' : 'child');
      datum.viaId = viaIdById.get(node.id);
      return datum;
    }),
  };

  return {
    tree,
    neighborCount: rankedNeighbors.length,
    edgeCount: keptEdges.length,
    edges: keptEdges,
  };
}

export function formatCanopyScore(score: number | null | undefined): string {
  if (typeof score !== 'number' || !Number.isFinite(score)) return '—';
  return score.toFixed(3);
}

export function resolveCanopyQueryId(row: Record<string, unknown> | null | undefined): string {
  if (!row) return '';
  const candidates = [
    row.diseaseUMLSCUI,
    row.disease_umls_cui,
    row.diseaseId,
    row.disease_id,
    row.conceptId,
    row.concept_id,
    row.id,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return '';
}

export function parseCanopyGraphPayload(payload: unknown): CanopyResponse | null {
  if (!payload || typeof payload !== 'object') return null;
  const data = payload as Record<string, unknown>;
  const query = typeof data.query === 'string' ? data.query.trim() : '';
  if (!query || !Array.isArray(data.nodes)) return null;

  const nodes: CanopyNode[] = data.nodes
    .filter((node): node is Record<string, unknown> => !!node && typeof node === 'object')
    .map(node => {
      const id = String(node.id ?? '').trim();
      const scoreRaw = node.score;
      const score =
        typeof scoreRaw === 'number' && Number.isFinite(scoreRaw)
          ? scoreRaw
          : scoreRaw == null
            ? null
            : Number.isFinite(Number(scoreRaw))
              ? Number(scoreRaw)
              : null;
      return {
        id,
        label: String(node.label ?? id),
        role: normalizeRole(node.role, id, query),
        score,
        inDisgenet: node.in_disgenet === true || node.inDisgenet === true,
      };
    })
    .filter(node => node.id);

  if (nodes.length === 0) return null;

  const edges: CanopyEdge[] = Array.isArray(data.edges)
    ? data.edges
        .filter((edge): edge is Record<string, unknown> => !!edge && typeof edge === 'object')
        .map(edge => ({
          parent: String(edge.parent ?? '').trim(),
          child: String(edge.child ?? '').trim(),
        }))
        .filter(edge => edge.parent && edge.child)
    : [];

  return { query, nodes, edges };
}

export function buildCanopyGraphEndpoint(
  queryId: string,
  limit: number = CANOPY_MAX_NEIGHBORS,
  isAuthenticated?: boolean,
): string {
  const params = new URLSearchParams({
    query: queryId,
    limit: String(limit),
  });
  if (!isAuthenticated) params.set('skey', 'unauth');
  return `api/ontology/graph?${params.toString()}`;
}
